import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useUser } from '../../contexts/UserContext'
import { API_CONFIG } from '../../config/apiConfig'
import axios from 'axios'
import { handleJSAPIAccess } from '../../utils/auth_access_util'
import { fileUploadSvc } from '../../utils/FileUploadSvc'
import { fileSvc } from '../../utils/FileSvc'
import './index.css'

/**
 * 意见反馈页
 *
 * 提供用户意见反馈提交功能：
 * - 反馈内容输入（必填）
 * - 联系方式输入（选填）
 * - 提交按钮 + loading 状态
 * - 调用 POST /api/v1/feedback/submit
 * - 提交成功后 toast 提示 + 跳转首页
 */
export default function Feedback() {
    const navigate = useNavigate()
    const { userInfo } = useUser()
    const [content, setContent] = useState('')
    const [contact, setContact] = useState('')
    const [images, setImages] = useState([]) // 待提交图片：{ id, path, size, name, uploaded, url, thumbnail }
    const [uploading, setUploading] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [toastMsg, setToastMsg] = useState(null)

    const showToast = (msg) => {
        setToastMsg(msg)
        setTimeout(() => setToastMsg(null), 2500)
    }

    const handleBack = () => {
        navigate('/')
    }

    // ============ 图片选择 / 上传 / 预览 ============

    // 单张图片上传到 OSS，返回 URL
    const uploadSingleImage = async (entry) => {
        const url = await fileUploadSvc.upload(
            { path: entry.path, size: entry.size, name: entry.name },
            'feedback',
            {},
            null
        )
        setImages(prev => prev.map(img =>
            img.id === entry.id ? { ...img, uploaded: true, url, thumbnail: url } : img
        ))
        return url
    }

    // 原生文件选择（非飞书环境 / PC 端 chooseImage 失败时兜底）
    const handleNativeFileSelect = () => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.multiple = true
        input.style.display = 'none'
        input.onchange = (e) => {
            const files = Array.from(e.target.files || [])
            if (files.length > 0) {
                const newImages = files.map(file => ({
                    id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
                    path: URL.createObjectURL(file),
                    size: file.size,
                    name: file.name || `image_${Date.now()}.jpg`,
                    uploaded: false,
                    url: '',
                    thumbnail: URL.createObjectURL(file)
                }))
                appendAndUpload(newImages)
            }
        }
        document.body.appendChild(input)
        input.click()
        document.body.removeChild(input)
    }

    // 追加图片并依次上传
    const appendAndUpload = (newImages) => {
        setImages(prev => [...prev, ...newImages])
        newImages.forEach(img => {
            uploadSingleImage(img).catch((err) => {
                console.error('[Feedback] 上传图片失败:', err)
                showToast('有图片上传失败，请重试', 'error')
            })
        })
    }

    const chooseImages = () => {
        const isPC = navigator.userAgent.includes('Windows') || navigator.userAgent.includes('Macintosh')
        const sourceType = isPC ? ['album'] : ['album', 'camera']

        const doChoose = () => {
            if (typeof fileSvc !== 'undefined' && fileSvc.chooseImage) {
                fileSvc.chooseImage({
                    count: 9,
                    sourceType: sourceType,
                    sizeType: ['compressed']
                }).subscribe(
                    (files) => {
                        console.log('[Feedback] 选择图片成功:', files)
                        const newImages = files.map(file => ({
                            id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
                            path: file.path,
                            size: file.size,
                            name: file.name || `image_${Date.now()}.jpg`,
                            uploaded: false,
                            url: '',
                            thumbnail: file.thumbnail || file.path
                        }))
                        appendAndUpload(newImages)
                    },
                    (error) => {
                        console.log('[Feedback] 选择图片失败:', error)
                        const errorMessage = error?.message || ''
                        if (errorMessage.includes('cancel') || errorMessage.includes('用户取消')) {
                            return
                        }
                        if (isPC) {
                            handleNativeFileSelect()
                        } else {
                            showToast(`选择图片失败: ${errorMessage}`, 'error')
                        }
                    }
                )
            } else {
                handleNativeFileSelect()
            }
        }

        if (window.h5sdk) {
            handleJSAPIAccess((isSuccess) => {
                if (isSuccess) doChoose()
                else handleNativeFileSelect()
            })
        } else {
            handleNativeFileSelect()
        }
    }

    const removeImage = (id) => {
        setImages(prev => prev.filter(img => img.id !== id))
    }

    const handlePreview = (current) => {
        const urls = images.map(img => img.uploaded ? img.url : img.path)
        const currentUrl = current.uploaded ? current.url : current.path
        if (typeof fileSvc !== 'undefined' && fileSvc.previewImage) {
            fileSvc.previewImage(urls, currentUrl)
                .then(() => console.log('[Feedback] 预览图片成功'))
                .catch(err => console.log('[Feedback] 预览图片失败:', err))
        }
    }

    const handleSubmit = async () => {
        // 表单校验：反馈内容必填
        if (!content || content.trim() === '') {
            showToast('请输入反馈内容')
            return
        }

        // 图片上传中 / 存在未上传完成的图片时，禁止提交
        if (uploading) {
            showToast('图片上传中，请稍候')
            return
        }
        const pendingImages = images.filter(img => !img.uploaded)
        if (pendingImages.length > 0) {
            showToast('请等待所有图片上传完成')
            return
        }

        setSubmitting(true)

        try {
            const tenantKey = userInfo?.tenant_key || API_CONFIG.tenantKey || ''
            const companyId = API_CONFIG.companyId
            const companyName = API_CONFIG.companyName || ''
            const userName = userInfo?.name || ''
            const openId = userInfo?.open_id || ''

            if (!openId) {
                showToast('未获取到用户信息，请重新进入')
                setSubmitting(false)
                return
            }

            // 已上传的图片 URL（逗号分隔）
            const imageUrls = images.filter(img => img.uploaded).map(img => img.url)
            const imagesStr = imageUrls.length > 0 ? imageUrls.join(',') : null

            const response = await axios.post(`${API_CONFIG.apiBaseUrl}/feedback/submit`, {
                tenantKey: tenantKey,
                companyId: companyId,
                companyName: companyName,
                userName: userName,
                openId: openId,
                content: content.trim(),
                contact: contact.trim() || null,
                images: imagesStr
            }, {
                headers: { 'Content-Type': 'application/json' }
            })

            if (response.data && response.data.code === 200) {
                showToast('感谢反馈')
                // 清空表单并跳转首页
                setTimeout(() => {
                    navigate('/')
                }, 1200)
            } else {
                showToast(response.data?.message || '提交失败，请稍后重试')
            }
        } catch (error) {
            console.error('[Feedback] 提交反馈失败:', error)
            showToast('网络异常，请稍后重试')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="feedback-page">
            <div className="feedback-header">

                <h2>意见反馈</h2>
            </div>

            <div className="feedback-form">
                <div className="form-group">
                    <label className="form-label">
                        反馈内容 <span className="required">*</span>
                    </label>
                    <textarea
                        className="form-textarea"
                        placeholder="请输入您的建议或意见，帮助我们做得更好..."
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        maxLength={500}
                        disabled={submitting}
                    />
                    <span className="form-count">{content.length}/500</span>
                </div>

                <div className="form-group">
                    <label className="form-label">联系方式（选填）</label>
                    <input
                        className="form-input"
                        type="text"
                        placeholder="手机号或邮箱，方便我们回复您"
                        value={contact}
                        onChange={(e) => setContact(e.target.value)}
                        maxLength={100}
                        disabled={submitting}
                    />
                </div>

                <div className="form-group">
                    <label className="form-label">图片（选填，最多9张）</label>
                    <div className="feedback-image-list">
                        {images.map((img) => (
                            <div
                                key={img.id}
                                className="feedback-image-item"
                                onClick={() => handlePreview(img)}
                            >
                                <img src={img.thumbnail || img.path} alt="反馈图片" />
                                {!img.uploaded && (
                                    <div className="feedback-image-mask">上传中</div>
                                )}
                                <button
                                    className="feedback-image-remove"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        removeImage(img.id)
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                        {images.length < 9 && (
                            <div className="feedback-image-add" onClick={chooseImages}>
                                <span className="feedback-image-add-icon">+</span>
                                <span>添加图片</span>
                            </div>
                        )}
                    </div>
                    {uploading && <div className="feedback-image-tip">图片上传中...</div>}
                </div>

                <button
                    className="submit-btn"
                    onClick={handleSubmit}
                    disabled={submitting || uploading}
                >
                    {submitting ? '提交中...' : (uploading ? '图片上传中...' : '提交反馈')}
                </button>
            </div>
            {/* Toast提示 */}
            {toastMsg && (
                <div className="toast-message">{toastMsg}</div>
            )}
            <div className="action-buttons">
                <button className="back-btn" onClick={handleBack}>返回首页</button>
            </div>
        </div>
    )
}
